import React from "react";
import { debounce } from "lodash";
import { Building2 } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import bancos from "../../lib/bancos.json";

const bankOptions = bancos.map((banco) => ({
  value: `${banco.codigo} - ${banco.nome}`,
}));

export function BankDataForm({ bancoData, handleBancoChange, errors, pixKeyInvalid, readOnly = false }) {
  const [actualBanks, setActualBanks] = React.useState(bankOptions);
  const [searchValue, setSearchValue] = React.useState("");

  const debouncedSearch = React.useMemo(
    () =>
      debounce((searchedBank) => {
        if (!searchedBank) {
          setActualBanks(bankOptions);
          return;
        }

        setActualBanks(
          bankOptions.filter((bank) =>
            bank.value.toLowerCase().includes(searchedBank.toLowerCase())
          )
        );
      }, 300),
    []
  );

  const handleBankSearch = React.useCallback(
    (e) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="mb-6 md:mb-10">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-blue-700 to-blue-600 flex items-center justify-center shadow-md shrink-0">
          <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>

        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
          Dados Bancários
        </h2>
      </div>

      <div className="space-y-4 md:space-y-6">
        {readOnly && (
          <div className="flex items-center gap-2 px-3 py-2 bg-statusWarningBg border border-statusWarning/20 rounded-lg text-xs text-statusWarning mb-2">
            <span>Atualização de dados bancários não está habilitada para este processo.</span>
          </div>
        )}
        <div>
          <Label htmlFor="nomeBanco" className="text-sm font-medium text-foreground">
            Nome do Banco <span className="text-red-500">*</span>
          </Label>

          <Select
            disabled={readOnly}
            onValueChange={(value) => handleBancoChange("nomeBanco", value)}
            value={bancoData.nomeBanco}
          >
            <SelectTrigger
              className={`w-full mt-2 border-2 h-11 focus:ring-2 focus:ring-blue-800/20 transition-colors ${
                errors.nomeBanco
                  ? "border-red-400 focus:border-red-600"
                  : "border-border focus:border-blue-800"
              }`}
            >
              <SelectValue placeholder="Selecione um banco..." />
            </SelectTrigger>

            <SelectContent className="flex">
              <div className="px-2 pt-2">
                <Input
                  value={searchValue}
                  onChange={handleBankSearch}
                  className="w-full"
                  placeholder="Buscar banco..."
                />
              </div>
              {actualBanks.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.nomeBanco && (
            <p className="mt-1 text-xs text-red-600">{errors.nomeBanco}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
          <div>
            <Label htmlFor="agencia" className="text-sm font-medium text-foreground">
              Agência <span className="text-red-500">*</span>
            </Label>

            <Input
              id="agencia"
              type="text"
              inputMode="numeric"
              placeholder="0000"
              value={bancoData.agencia}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/[^0-9\-xX]/g, "");
                handleBancoChange("agencia", onlyNumbers);
              }}
              required
              disabled={readOnly}
              className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${readOnly ? "opacity-60 cursor-not-allowed" : ""} ${
                errors.agencia
                  ? "border-red-400 focus:border-red-600"
                  : "border-border focus:border-blue-800"
              }`}
            />

            {errors.agencia && (
              <p className="mt-1 text-xs text-red-600">{errors.agencia}</p>
            )}
          </div>

          <div>
            <Label htmlFor="conta" className="text-sm font-medium text-foreground">
              Conta com dígito <span className="text-red-500">*</span>
            </Label>

            <Input
              id="conta"
              type="text"
              inputMode="numeric"
              placeholder="00000-0"
              value={bancoData.conta}
              onChange={(e) => {
                const onlyNumbersAndHyphen = e.target.value.replace(
                  /[^0-9\-xX]/g,
                  ""
                );
                handleBancoChange("conta", onlyNumbersAndHyphen);
              }}
              required
              disabled={readOnly}
              className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${readOnly ? "opacity-60 cursor-not-allowed" : ""} ${
                errors.conta
                  ? "border-red-400 focus:border-red-600"
                  : "border-border focus:border-blue-800"
              }`}
            />

            {errors.conta && (
              <p className="mt-1 text-xs text-red-600">{errors.conta}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="tipoPix" className="text-sm font-medium text-foreground">
            Tipo de Chave PIX
          </Label>

          <Select
            disabled={readOnly}
            value={bancoData.tipoPix}
            onValueChange={(value) => handleBancoChange("tipoPix", value)}
          >
            <SelectTrigger className="w-full mt-2 border-2 border-border focus:border-blue-800 h-11 focus:ring-2 focus:ring-blue-800/20">
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="cpf">CPF</SelectItem>
              <SelectItem value="email">E-mail</SelectItem>
              <SelectItem value="telefone">Telefone</SelectItem>
              <SelectItem value="aleatorio">Chave Aleatória</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="chavePix" className="text-sm font-medium text-foreground">
            Chave PIX
          </Label>

          <Input
            id="chavePix"
            placeholder="Sua chave PIX"
            value={bancoData.chavePix}
            onChange={(e) => handleBancoChange("chavePix", e.target.value)}
            disabled={readOnly}
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${readOnly ? "opacity-60 cursor-not-allowed" : ""} ${
              pixKeyInvalid
                ? "border-red-400 focus:border-red-600"
                : "border-border focus:border-blue-800"
            }`}
          />

          {pixKeyInvalid && (
            <p className="mt-1 text-xs text-red-600">
              Chave PIX inválida para o tipo selecionado.
            </p>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium text-foreground block mb-3">
            Tipo de Conta <span className="text-red-500">*</span>
          </Label>

          <RadioGroup
            value={bancoData.tipoConta}
            onValueChange={(value) => handleBancoChange("tipoConta", value)}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div
              className={`flex items-center space-x-3 border-2 rounded-xl p-5 cursor-pointer transition-all ${
                bancoData.tipoConta === "corrente"
                  ? "border-primary bg-statusInfoBg shadow-md ring-2 ring-blue-800/20"
                  : errors.tipoConta
                  ? "border-red-400"
                  : "border-border hover:border-blue-600 hover:bg-statusInfoBg/30"
              }`}
              onClick={() => handleBancoChange("tipoConta", "corrente")}
            >
              <RadioGroupItem
                value="corrente"
                id="corrente"
                className="w-5 h-5"
              />
              <Label
                htmlFor="corrente"
                className="cursor-pointer flex-1 font-medium"
              >
                Conta Corrente
              </Label>
            </div>

            <div
              className={`flex items-center space-x-3 border-2 rounded-xl p-5 cursor-pointer transition-all ${
                bancoData.tipoConta === "poupanca"
                  ? "border-primary bg-statusInfoBg shadow-md ring-2 ring-blue-800/20"
                  : errors.tipoConta
                  ? "border-red-400"
                  : "border-border hover:border-blue-600 hover:bg-statusInfoBg/30"
              }`}
              onClick={() => handleBancoChange("tipoConta", "poupanca")}
            >
              <RadioGroupItem
                value="poupanca"
                id="poupanca"
                className="w-5 h-5"
              />
              <Label
                htmlFor="poupanca"
                className="cursor-pointer flex-1 font-medium"
              >
                Conta Poupança
              </Label>
            </div>
          </RadioGroup>

          {errors.tipoConta && (
            <p className="mt-1 text-xs text-red-600">{errors.tipoConta}</p>
          )}
        </div>
      </div>
    </div>
  );
}
